import { getAmericanTime } from '@helpers/getAmericanTimezone';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';

import { formatAuditJson } from './helpers/formatAuditJson';
import { useDrawerDismiss } from './helpers/useDrawerDismiss';
import { TProps } from './types/TProps';

export const DiffDrawer = ({ auditLog, onClose }: TProps) => {
  const containerRef = useDrawerDismiss({ isOpen: Boolean(auditLog), onClose });

  if (!auditLog) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-end bg-[var(--color-overlay)]">
      <p
        aria-hidden="true"
        className="hidden rotate-180 select-none whitespace-nowrap px-2 text-sm text-[var(--color-fixed-white)] [writing-mode:vertical-rl] [text-orientation:mixed] lg:block"
      >
        Click to Close
      </p>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Audit entry ${auditLog.id}`}
        tabIndex={-1}
        className="flex h-full w-full max-w-[900px] flex-col overflow-y-auto bg-surface shadow-[0_8px_10px_var(--color-shadow)] outline-none"
      >
        <div className="sticky top-0 z-[1] flex items-center justify-between gap-4 border-b border-[var(--color-border)] bg-surface px-6 py-4">
          <h2 className="text-lg font-medium text-foreground">Audit entry</h2>
        </div>

        <dl className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Time</dt>
            <dd className="m-0 text-sm text-foreground">{getAmericanTime(auditLog.createdAt, true, true)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Entity</dt>
            <dd className="m-0 break-all text-sm text-foreground">
              {auditLog.entityType} <span className="text-foreground-muted">{auditLog.entityId}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Action</dt>
            <dd className="m-0 text-sm text-foreground">
              {auditLog.action} <span className="text-foreground-muted">{auditLog.operation}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Business action</dt>
            <dd className="m-0 text-sm text-foreground">{auditLog.businessAction ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Actor</dt>
            <dd className="m-0 break-all text-sm text-foreground">
              {auditLog.actorLabel ?? '—'} <span className="text-foreground-muted">{auditLog.actorType}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-foreground-muted">Request id</dt>
            <dd className="m-0 break-all text-sm text-foreground">{auditLog.requestId ?? '—'}</dd>
          </div>
        </dl>

        <div className="px-6 pb-5">
          <h3 className="text-sm font-semibold text-foreground">Changed fields</h3>
          {auditLog.changedFields.length ? (
            <ul className="mt-2 flex flex-wrap gap-2 p-0">
              {auditLog.changedFields.map((field) => (
                <li
                  key={field}
                  className="list-none rounded-[5px] bg-surface-muted px-2 py-1 text-xs text-foreground-muted"
                >
                  {field}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-foreground-muted">No field changes recorded.</p>
          )}
        </div>

        <div className="px-6 pb-8">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Before / after</h3>
          <ReactDiffViewer
            oldValue={formatAuditJson(auditLog.before)}
            newValue={formatAuditJson(auditLog.after)}
            splitView={true}
            useDarkTheme={false}
            compareMethod={DiffMethod.WORDS}
            leftTitle="Before"
            rightTitle="After"
          />
        </div>
      </div>
    </div>
  );
};
